/**
 * Shared renderers for the catalog metadata a model row carries.
 *
 * ONE source for both consumers — the Providers models table and the chat
 * header's ModelCapabilitiesDisplay. They must render the same pill
 * set from the same fields; two hand-written copies would drift the moment a
 * flag is added, and the tri-state rule below is exactly the kind of thing that
 * gets got right in one copy and wrong in the other.
 *
 * ─── TRI-STATE DISCIPLINE ────────────────────────────────────────────────
 * A capability flag is `boolean | undefined`. `undefined` means the PROVIDER WAS
 * SILENT and must render as ABSENT — never as a negative. The Go type is `*bool`
 * for precisely this reason (agents ADR-003). Rendering "no vision" for a
 * provider that simply does not publish a vision field is a claim the product
 * cannot source, and it is the failure mode this component exists to prevent.
 *
 * The lifecycle `status` is likewise NOT derived here: it arrives already
 * computed server-side, so a date comparison never lands in a renderer.
 */

import type { ModelLifecycleStatus, ModelPricing } from '@localagent/sdk';

/** The tri-state capability inputs both consumers share. */
export interface ModelCapabilityFlags {
  supports_tools?: boolean;
  supports_vision?: boolean;
  supports_reasoning?: boolean;
  supports_structured_outputs?: boolean;
}

const PILLS: ReadonlyArray<{ key: keyof ModelCapabilityFlags; label: string; testid: string }> = [
  { key: 'supports_tools', label: 'Tools', testid: 'tools' },
  { key: 'supports_vision', label: 'Vision', testid: 'vision' },
  { key: 'supports_reasoning', label: 'Reasoning', testid: 'reasoning' },
  { key: 'supports_structured_outputs', label: 'Structured', testid: 'structured' },
];

/**
 * Capability pills. A flag that is `undefined` renders NOTHING (not a struck-out
 * or greyed pill) — absence of evidence is not evidence of absence.
 */
export function ModelCapabilityPills({ flags }: { flags: ModelCapabilityFlags }) {
  const known = PILLS.filter(({ key }) => flags[key] !== undefined);
  if (known.length === 0) {
    return (
      <span data-testid="model-capability-pills" className="text-caption text-muted-foreground">
        —
      </span>
    );
  }
  return (
    <span data-testid="model-capability-pills" className="flex flex-wrap gap-1">
      {known.map(({ key, label, testid }) => {
        const supported = flags[key] === true;
        return (
          <span
            key={key}
            data-testid={`model-capability-${testid}`}
            data-supported={String(supported)}
            className={
              'rounded px-1.5 py-0.5 text-micro font-medium ' +
              (supported
                ? 'bg-success-subtle text-success-text'
                : 'bg-muted text-muted-foreground line-through')
            }
          >
            {label}
          </span>
        );
      })}
    </span>
  );
}

/** Lifecycle badge. `active` renders nothing — a badge on every row is noise. */
export function ModelStatusBadge({
  status,
  deprecationDate,
}: {
  status: ModelLifecycleStatus;
  deprecationDate?: string | null;
}) {
  if (status === 'active') {
    return (
      <span data-testid="model-status" data-status="active" className="text-caption text-muted-foreground">
        Active
      </span>
    );
  }
  const retired = status === 'retired';
  const date = deprecationDate ? new Date(deprecationDate).toISOString().slice(0, 10) : undefined;
  return (
    <span
      data-testid="model-status"
      data-status={status}
      title={date ? `${retired ? 'Retired' : 'Retires'} ${date}` : undefined}
      className={
        'rounded px-1.5 py-0.5 text-micro font-medium ' +
        (retired
          ? 'bg-error-subtle text-error-text'
          : 'bg-warning-subtle text-warning-text')
      }
    >
      {retired ? 'Retired' : 'Deprecated'}
      {date ? ` · ${date}` : ''}
    </span>
  );
}

/** USD per million tokens, as the catalog stores it. */
export function ModelPricingCell({ pricing }: { pricing?: ModelPricing }) {
  if (!pricing || (pricing.input_per_mtok == null && pricing.output_per_mtok == null)) {
    return (
      <span data-testid="model-pricing" className="text-caption text-muted-foreground">
        —
      </span>
    );
  }
  const fmt = (v?: number | null) => (v == null ? '—' : `$${v}`);
  return (
    <span data-testid="model-pricing" className="text-caption text-muted-foreground">
      {fmt(pricing.input_per_mtok)} in / {fmt(pricing.output_per_mtok)} out per Mtok
    </span>
  );
}

/** Controlled-vocabulary modality list; empty renders nothing. */
export function ModelModalities({ input, output }: { input?: string[]; output?: string[] }) {
  if ((input?.length ?? 0) === 0 && (output?.length ?? 0) === 0) return null;
  return (
    <span data-testid="model-modalities" className="text-caption text-muted-foreground">
      {(input ?? []).join(', ')} → {(output ?? []).join(', ')}
    </span>
  );
}
